import React, { useState, useEffect } from 'react';
import styles from './DerivedChannels.module.css';
import AddChannelModal from './AddChannelModal';
import { AiOutlinePlus } from 'react-icons/ai';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import tableInitWorker from '../../../../../../Utils/restructure.worker';
import WorkerBuilder from '../../../../../../Utils/builder.worker';
import valueExtractorWorker from '../../../../../../Utils/valueExtractor.worker';
import {
  fetchChannelData,
  deleteChannel,
} from '../../../../../../redux/slices/derivedChannelSlice';
import portlessProxy from 'bareproxy';
import { useToast } from '@chakra-ui/react';

const io = require('socket.io-client');
const tableInitializer = new WorkerBuilder(tableInitWorker);
const valueExtractor = new WorkerBuilder(valueExtractorWorker);

const Derivedchannels = () => {
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [modalType, setModalType] = useState(false); // Determines if modal is for 'Add' or 'Edit'
  const [channels, setChannels] = useState([]); // Stores channels
  const [editingChannelIndex, setEditingChannelIndex] = useState(null); // Tracks channel index for editing
  const [value, setValue] = useState(''); // Stores calculated value
  const [expression, setExpression] = useState(''); // Stores expression for calculation
  // const [data, setData] = useState([]); // Stores fetched data
  const [channelNames, setChannelNames] = useState([]); // Stores channel names
  const [tableData, setTableData] = useState([]); // Stores data for table
  const [dateTimesLive, setDateTimesLive] = useState([]); // Stores live data timestamps
  const [dataArrLive, setDataArrLive] = useState([]); // Stores live data array
  const [isWorkerBusy, setIsWorkerBusy] = useState(false); // Indicates if worker is processing data
  const [socketData, setSocketData] = useState(null); // Stores data from socket
  const [init, setInit] = useState(false); // Initialization flag
  const [searchParams] = useSearchParams(); // Gets search params from URL
  const [selectedCardId, setSelectedCardId] = useState(''); // Tracks selected card ID
  const [selectedCardName, setSelectedCardName] = useState(''); // Tracks selected card name
  const [selectedCardValue, setSelectedCardValue] = useState(''); // Tracks selected card value
  const uniqueProjectId = searchParams.get('project'); // Gets unique project ID from URL
  const [socket, setSocket] = useState(io(portlessProxy, { // Initializes socket connection
    cors: {
      origin: '*',
      allowedHeaders: ['*'],
      allowedMethods: ['GET', 'POST'],
      insecure: true,
    },
  }));

  const channelData = useSelector((state) => state.derivedChannel.channelData); // Redux state for channel data
  const dispatch = useDispatch();
  const email = localStorage.getItem('email'); // Retrieves user email from localStorage
  const data = useSelector((state) => state.viewData.graphData);


  useEffect(() => {
    // Socket connection and event listeners setup
    const data = { email: email, projectId: uniqueProjectId };
    socket.on('connect', () => {
      toast({
        title: `Successfully connected with Server`,
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    });
    socket.emit('joinRoom', `${uniqueProjectId}`);
    socket.on('message', (data) => {
      const newData = JSON.parse(data);
      newData.projectId = uniqueProjectId;
      socket.emit("clientMessage", newData);
    });

    socket.on('liveCalutationData', (data) => {
      setSocketData(data); // Updates socket data on event
    });

    // Fetch channel data
    const token = localStorage.getItem("token")
    try {
      dispatch(fetchChannelData({data, token}));
    } catch (error) {
      console.log(error);
    }
  }, []); // Runs only once on component mount

  useEffect(() => {
    // Fetch and process live data for table and chart
    const fetchData = async () => {
      try {
        if (data.length > 0) {
          const currentTime = new Date();
          currentTime.setTime(currentTime.getTime() - 1 * 60 * 60 * 1000); // Subtract 1 hour
          const liveDataFiltered = data.filter(
            (dataset) => new Date(dataset.time) >= currentTime,
          );

          // Restructure data for table visualization
          const tableDataArrayGen = async () => {
            const dataPromise = new Promise((resolve) => {
              tableInitializer.onmessage = (e) => {
                resolve(e.data);
              };
            });
            tableInitializer.postMessage({ data: liveDataFiltered });
            return await dataPromise;
          };
          const tableDataArray = await tableDataArrayGen();
          let gotHeaders = [];
          if (tableDataArray.data) {
            gotHeaders = tableDataArray.headers;
            const arrayOfValueArrays = tableDataArray.channelWiseData;

            const getNewDataArr = () =>
              gotHeaders.map((header, index) => ({
                label: header,
                data: arrayOfValueArrays[index],
                fill: false,
              }));

            const newDataArr = getNewDataArr();
            setChannelNames(gotHeaders);
            setTableData(tableDataArray.data);
            setDataArrLive(newDataArr);
            setDateTimesLive(tableDataArray.dateTimeArray);
            setInit(true);
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [data]); // Runs when 'data' changes

  useEffect(() => {
    // Updates live data when socket data changes
    const updateData = async () => {
      if (socketData && init && !isWorkerBusy) {
        setIsWorkerBusy(true);
        const structuredData = async () => {
          const dataPromise = new Promise((resolve) => {
            valueExtractor.onmessage = (e) => {
              resolve(e.data);
            };
          });
          valueExtractor.postMessage({
            socketData: socketData,
            currentTable: tableData,
            dateTimeArray: dateTimesLive,
            dataArray: dataArrLive,
            headers: channelNames,
            duration: 1,
          });
          return await dataPromise;
        };

        const workerResponse = await structuredData();
        if (workerResponse.updatedTable) {
          setChannelNames(workerResponse.updatedHeaders);
          setTableData(workerResponse.updatedTable);
          setDataArrLive(workerResponse.updatedLiveArray);
          setDateTimesLive(workerResponse.updatedDateTimeArray);
        }
      }
    };
    updateData();
    setIsWorkerBusy(false); // Reset worker state
  }, [socketData]); // Runs when 'socketData' changes

  // Modal handling functions
  const handleEditModal = (index) => {
    setIsModalOpen(true);
    setModalType('Edit');
    setEditingChannelIndex(index);
  };

  const handleAddModal = () => {
    setIsModalOpen(true);
    setModalType('Add');
    setEditingChannelIndex(null);
  };

  const handleSaveChannel = (newChannel) => {
    // Saves a new or updated channel
    if (editingChannelIndex !== null) {
      const updatedChannels = [...channels];
      updatedChannels[editingChannelIndex] = newChannel;
      setChannels(updatedChannels);
    } else {
      setChannels([...channels, newChannel]);
    }
    setIsModalOpen(false); // Close modal after save
  };

  const handleDeleteChannel = (id) => {
    // Deletes a channel
    const data = { email: email, projectId: uniqueProjectId, _id: id , token : localStorage?.getItem("token") };
    try {
      dispatch(deleteChannel(data)).then((response) => {
        if (response.payload.status === 200) {
          toast({
            title: `Derived channel deleted successfully`,
            status: 'success',
            position: 'top-right',
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: `Channel has not been deleted.`,
            status: 'error',
            position: 'top-right',
            duration: 3000,
            isClosable: true,
          });
        }
      });
    } catch (error) {
      console.log(error);
      toast({
        title: "Internal Server Error",
        status: "error",
        position: "top-right",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const calculateValue = (expr) => {
    // Evaluates expression and updates the value state
    try {
      const result = eval(expr);
      setValue(result);
    } catch (error) {
      setValue('Error');
    }
  };

  const editChannel = (index, id, channelName, channelValue) => {
    handleEditModal(index);
    setSelectedCardId(id);
    setSelectedCardName(channelName);
    setSelectedCardValue(channelValue);
  };


  return (
    <div className={styles.container}>
      <div className={styles.cards}>
        {channelData?.map((channel, index) => (
          <div className={styles.card} key={index}>
            <div className={styles.firstHalf}>
              <div className={styles.name}>{channel.channelName}</div>
              <div className={styles.value}>
                value:
                <span className={styles.number}>{socketData && socketData[channel?._id] || channel?.channelValue}</span>
              </div>
            </div>
            <div className={styles.secondHalf}>
              <button
                className={styles.delete}
                onClick={() => handleDeleteChannel(channel._id)}
              >
                Delete
              </button>
              <button
                className={styles.edit}
                onClick={() =>
                  editChannel(
                    index,
                    channel._id,
                    channel.channelName,
                    channel.channelValue,
                  )
                }
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
      <button className={styles.addChannel} onClick={handleAddModal}>
        <AiOutlinePlus />
        Add Channel
      </button>
      {isModalOpen && (
        <AddChannelModal
          setIsModalOpen={setIsModalOpen}
          modalType={modalType}
          onSave={handleSaveChannel}
          editingChannel={
            editingChannelIndex !== null ? channels[editingChannelIndex] : null
          }
          expression={expression}
          setExpression={setExpression}
          calculateValue={calculateValue}
          channelNames={channelNames}
          dataArrLive={dataArrLive}
          value={value}
          email={email}
          uniqueProjectId={uniqueProjectId}
          selectedCardId={selectedCardId}
          selectedCardName={selectedCardName}
          selectedCardValue={selectedCardValue}
        />
      )}
    </div>
  );
};

export default Derivedchannels;
